import React, { useState, useEffect } from 'react';
import { AlertTriangle, DollarSign, TrendingUp, Clock, Info } from 'lucide-react';

const TimeMaterialsProposalManager = () => {
    // Project data
    const estimatedTotalHours = 320;
    const targetHourlyRate = 75;
    const projectStartDate = '2025-07-01';
    const projectEndDate = '2025-08-26';

    // Time & Materials proposal state
    const [availableStartDate, setAvailableStartDate] = useState(projectStartDate);
    const [hoursPerWeek, setHoursPerWeek] = useState(40);
    const [hourlyRate, setHourlyRate] = useState(targetHourlyRate);

    // Calculated values
    const [weeklyPayout, setWeeklyPayout] = useState(0);
    const [totalProposedPayout, setTotalProposedPayout] = useState(0);
    const [isOverBudget, setIsOverBudget] = useState(false);
    const [projectWeeks, setProjectWeeks] = useState(0);

    // Calculate project duration and totals
    useEffect(() => {
        const start = new Date(availableStartDate);
        const end = new Date(projectEndDate);
        const diffTime = Math.abs(end - start);
        const diffWeeks = Math.ceil(diffTime / (1000 * 60 * 60 * 24 * 7));
        setProjectWeeks(diffWeeks);

        const weekly = hoursPerWeek * hourlyRate;
        const total = weekly * diffWeeks;
        const estimatedCost = estimatedTotalHours * targetHourlyRate;

        setWeeklyPayout(weekly);
        setTotalProposedPayout(total);
        setIsOverBudget(total > estimatedCost);
    }, [availableStartDate, hoursPerWeek, hourlyRate, estimatedTotalHours, targetHourlyRate]);

    const handleStartDateChange = (e) => {
        setAvailableStartDate(e.target.value);
    };

    const handleHoursPerWeekChange = (e) => {
        const value = parseFloat(e.target.value) || 0;
        setHoursPerWeek(value);
    };

    const handleRateChange = (e) => {
        const value = parseFloat(e.target.value) || 0;
        setHourlyRate(value);
    };

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD',
            minimumFractionDigits: 0,
            maximumFractionDigits: 0,
        }).format(amount);
    };

    const formatHourlyRate = (rate) => {
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD',
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        }).format(rate);
    };

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
        });
    };

    const estimatedCost = estimatedTotalHours * targetHourlyRate;
    const percentageOfBudget = ((totalProposedPayout / estimatedCost) * 100).toFixed(1);
    const totalHours = hoursPerWeek * projectWeeks;

    return (
        <div className="max-w-2xl mx-auto p-6 bg-white shadow-lg">
            <div className="mb-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-2">Propose Your Payout</h2>
                <p className="text-gray-600">Time & Materials Engagement</p>
            </div>

            {/* Project Overview */}
            <div className="bg-gray-50 p-6 mb-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                    <DollarSign className="w-5 h-5 mr-2 text-green-600" />
                    Project Overview
                </h3>

                <div className="flex flex-wrap justify-between gap-6">
                    <div className="text-center">
                        <div className="text-2xl font-bold text-green-600">{formatCurrency(estimatedCost)}</div>
                        <div className="text-sm text-gray-600">Estimated Payout</div>
                    </div>
                    <div className="text-center">
                        <div className="text-2xl font-bold text-blue-600">{formatHourlyRate(targetHourlyRate)}</div>
                        <div className="text-sm text-gray-600">Target Hourly Rate</div>
                    </div>
                    <div className="text-center">
                        <div className="text-2xl font-bold text-purple-600">{estimatedTotalHours}hrs</div>
                        <div className="text-sm text-gray-600">8 weeks (Jul 1 - Aug 26)</div>
                    </div>
                </div>
            </div>

            {/* Explanation */}
            <div className="bg-green-50 border border-green-200 p-4 mb-6">
                <div className="flex items-start">
                    <Info className="w-5 h-5 text-green-600 mr-3 mt-0.5 flex-shrink-0" />
                    <div>
                        <h4 className="text-green-800 font-semibold mb-1">Time & Materials Engagement</h4>
                        <p className="text-green-700 text-sm">
                            This is a time-based project where you'll be paid for actual hours worked. Please specify your availability start date,
                            weekly hour commitment, and desired hourly rate. Payment will be based on hours logged.
                        </p>
                    </div>
                </div>
            </div>

            {/* Time & Materials Inputs */}
            <div className="space-y-6 mb-6">
                <div>
                    <label htmlFor="start-date" className="block text-lg font-semibold text-gray-900 mb-3">
                        Available Start Date
                    </label>
                    <input
                        id="start-date"
                        type="date"
                        value={availableStartDate}
                        onChange={handleStartDateChange}
                        className="block w-full px-4 py-4 text-xl font-medium border-2 border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                    />
                </div>

                <div className="flex flex-wrap gap-6">
                    <div className="flex-1 min-w-64">
                        <label htmlFor="hours-week" className="block text-lg font-semibold text-gray-900 mb-3">
                            Available Hours per Week
                        </label>
                        <div className="relative">
                            <input
                                id="hours-week"
                                type="number"
                                value={hoursPerWeek}
                                onChange={handleHoursPerWeekChange}
                                className="block w-full px-4 py-4 text-xl font-medium border-2 border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                                placeholder="40"
                            />
                            <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                                <Clock className="w-5 h-5 text-gray-400" />
                            </div>
                        </div>
                    </div>

                    <div className="flex-1 min-w-64">
                        <label htmlFor="hourly-rate" className="block text-lg font-semibold text-gray-900 mb-3">
                            Desired Hourly Rate
                        </label>
                        <div className="relative">
                            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                <span className="text-gray-500 text-lg">$</span>
                            </div>
                            <input
                                id="hourly-rate"
                                type="number"
                                step="0.01"
                                value={hourlyRate}
                                onChange={handleRateChange}
                                className="block w-full pl-8 pr-4 py-4 text-xl font-medium border-2 border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                                placeholder="0.00"
                            />
                        </div>
                    </div>
                </div>
            </div>

            {/* Proposal Summary */}
            <div className={`p-6 mb-6 border-2 ${isOverBudget ? 'bg-red-50 border-red-200' : 'bg-blue-50 border-blue-200'}`}>
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                    <TrendingUp className="w-5 h-5 mr-2 text-blue-600" />
                    Your Proposal Summary
                </h3>

                <div className="flex flex-wrap justify-between gap-6 mb-4">
                    <div className="text-center">
                        <div className="text-sm text-gray-600 mb-1">Payout per week</div>
                        <div className="text-2xl font-bold text-gray-900">{formatCurrency(weeklyPayout)}</div>
                        <div className="text-xs text-gray-500">
                            {hoursPerWeek}hrs × {formatHourlyRate(hourlyRate)}
                        </div>
                    </div>
                    <div className="text-center">
                        <div className="text-sm text-gray-600 mb-1">Total proposed payout</div>
                        <div className={`text-2xl font-bold ${isOverBudget ? 'text-red-600' : 'text-green-600'}`}>
                            {formatCurrency(totalProposedPayout)}
                        </div>
                        <div className="text-sm text-gray-500">
                            {percentageOfBudget}% of estimated cost
                        </div>
                    </div>
                </div>

                <div className="pt-4 border-t border-gray-200">
                    <div className="text-sm text-gray-600 space-y-1">
                        <div>Payment schedule: {formatCurrency(weeklyPayout)} × {projectWeeks} weeks = {formatCurrency(totalProposedPayout)}</div>
                        <div>Total time commitment: {totalHours} hours over {projectWeeks} weeks</div>
                        <div>Effective hourly rate: {formatHourlyRate(hourlyRate)}</div>
                        <div>Project duration: {formatDate(availableStartDate)} to {formatDate(projectEndDate)} ({projectWeeks} weeks)</div>
                    </div>
                </div>
            </div>

            {/* Warning for over-budget */}
            {isOverBudget && (
                <div className="bg-red-50 border border-red-200 p-4 mb-6">
                    <div className="flex items-start">
                        <AlertTriangle className="w-5 h-5 text-red-600 mr-3 mt-0.5 flex-shrink-0" />
                        <div>
                            <h4 className="text-red-800 font-semibold mb-1">Proposal exceeds estimated cost</h4>
                            <p className="text-red-700 text-sm">
                                Your total proposed payout of {formatCurrency(totalProposedPayout)} exceeds the estimated project cost by {formatCurrency(totalProposedPayout - estimatedCost)}.
                                Consider adjusting your weekly hours or hourly rate to align with the estimated cost.
                            </p>
                        </div>
                    </div>
                </div>
            )}

            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row gap-3">
                <button
                    disabled={isOverBudget}
                    className={`flex-1 py-3 px-6 font-semibold transition-colors ${
                        isOverBudget
                            ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                            : 'bg-blue-600 text-white hover:bg-blue-700 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2'
                    }`}
                >
                    Submit Proposal
                </button>
                <button className="flex-1 py-3 px-6 bg-gray-100 text-gray-700 font-semibold hover:bg-gray-200 transition-colors">
                    Save as Draft
                </button>
            </div>
        </div>
    );
};

export default TimeMaterialsProposalManager;